import React, { useState, useEffect } from 'react';
import '@/App.css';
import { Menu, X, ArrowRight, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { BrowserRouter, Routes, Route, Link, useNavigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import LoginPage from './pages/LoginPage';
import AdminPanel from './pages/AdminPanel';
import ArchivePage from './pages/ArchivePage';
import AiOperatingSystem from './pages/AiOperatingSystem';
import AskCAACI from './components/AskCAACI';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default content (fallback if API fails)
const defaultContent = {
  hero_title: "Intelligence to Impact",
  hero_subtitle: "Centre for Applied AI — Australia's leading applied research centre transforming enterprises through research-driven AI innovation",
  hero_cta_primary: "Stay Connected",
  hero_cta_secondary: "Explore Research",
  about_title: "About CAACI",
  about_description: "The Centre for Applied AI (CAACI) bridges the gap between cutting-edge AI research and practical enterprise implementation. Based in Geelong and Melbourne, we partner with organizations to unlock the transformative potential of artificial intelligence.",
  about_mission_title: "Our Mission",
  about_mission_text: "To democratize AI adoption by providing research-backed strategies, hands-on implementation support, and continuous innovation partnerships that deliver measurable business outcomes.",
  about_vision_title: "Our Vision",
  about_vision_text: "To be Australia's premier applied AI research centre, recognized globally for translating AI breakthroughs into enterprise value.",
  services_title: "Our Services",
  services_subtitle: "Comprehensive AI solutions tailored to your organization's unique challenges",
  service_1_title: "AI Strategy & Research Advisory",
  service_1_description: "Evidence-based AI roadmaps aligned with your business objectives. We analyze your operations, identify high-impact AI opportunities, and develop actionable implementation strategies.",
  service_1_bullet_1: "AI readiness assessment",
  service_1_bullet_2: "Strategic roadmap development",
  service_1_bullet_3: "Use case identification & prioritization",
  service_1_bullet_4: "ROI modeling & impact analysis",
  service_2_title: "AI Integration & Implementation",
  service_2_description: "End-to-end AI deployment from proof-of-concept to production. Our team ensures seamless integration with existing systems while maximizing ROI and minimizing disruption.",
  service_2_bullet_1: "Custom AI solution development",
  service_2_bullet_2: "Legacy system integration",
  service_2_bullet_3: "Process automation",
  service_2_bullet_4: "Change management & training",
  service_3_title: "Applied Research & Innovation",
  service_3_description: "Custom AI research projects addressing your specific challenges. We bring academic rigor and practical focus to develop novel solutions that advance your competitive position.",
  service_3_bullet_1: "Industry-specific AI research",
  service_3_bullet_2: "Proof of concept development",
  service_3_bullet_3: "Performance optimization",
  service_3_bullet_4: "Continuous innovation programs",
  research_title: "Research Publications & Insights",
  research_subtitle: "Leading-edge research from our applied centre on AI implementation and enterprise transformation",
  resources_title: "Curated AI Resources",
  resources_subtitle: "Essential AI resources and guidelines for Australian enterprises",
  contact_title: "Connect with Our Research Centre",
  contact_subtitle: "Join our network to receive exclusive research publications, invitations to research collaborations, and updates on AI innovations from CAACI",
  contact_cta: "Connect With CAACI",
  footer_tagline: "Intelligence to Impact",
  footer_description: "CAACI (pronounced Kay-See) — Centre for Applied AI — Australia's leading applied research centre transforming enterprises through research-driven AI innovation.",
  footer_location: "Geelong & Melbourne",
  footer_region: "Victoria, Australia",
  footer_copyright: "© 2026 Centre for Applied AI. All rights reserved.",
  nav_services: "Services",
  nav_research: "Research",
  nav_resources: "Resources",
  nav_about: "About",
  nav_connect: "Connect",
};

const HomePage = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [articles, setArticles] = useState([]);
  const [resources, setResources] = useState([]);
  const [content, setContent] = useState(defaultContent);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    message: '',
    interests: ''
  });
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchSiteContent();
    fetchLatestArticles();
    fetchResources();
  }, []);

  // Handle hash navigation on page load
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const sectionId = hash.replace('#', '');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 500);
    }
  }, []);

  const fetchSiteContent = async () => {
    try {
      const response = await axios.get(`${API}/content`);
      setContent({ ...defaultContent, ...response.data });
    } catch (error) {
      console.error('Error fetching site content:', error);
      // Keep default content on error
    }
  };

  const fetchResources = async () => {
    try {
      const response = await axios.get(`${API}/resources?active_only=true`);
      setResources(response.data);
    } catch (error) {
      console.error('Error fetching resources:', error);
      setResources([]);
    }
  };

  const fetchLatestArticles = async () => {
    try {
      const response = await axios.get(`${API}/articles/latest?limit=4`);
      setArticles(response.data);
    } catch (error) {
      console.error('Error fetching articles:', error);
      // If no articles in database, show empty array
      setArticles([]);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Research Paper': 'bg-green-100 text-green-800',
      'Industry Report': 'bg-blue-100 text-blue-800',
      'White Paper': 'bg-purple-100 text-purple-800',
      'Guide': 'bg-orange-100 text-orange-800',
      'Case Study': 'bg-red-100 text-red-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  // Smooth scroll to section
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const handleFormChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });
    
    try {
      await axios.post(`${API}/register`, {
        name: formData.name,
        email: formData.email,
        organization: formData.organization || null,
        message: formData.message || null,
        interests: formData.interests || null
      });
      
      setSubmitStatus({
        type: 'success',
        message: 'Thank you for registering! We will be in touch soon.'
      });
      setFormData({ name: '', email: '', organization: '', message: '', interests: '' });
    } catch (error) {
      console.error('Registration error:', error);
      setSubmitStatus({
        type: 'error',
        message: error.response?.data?.detail || 'Something went wrong. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="App min-h-screen bg-white">
      {/* Navigation Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200" data-testid="main-navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex-shrink-0" data-testid="logo">
              <button onClick={() => scrollToSection('hero')} className="text-xl sm:text-2xl font-bold text-blue-600 hover:text-blue-700 transition-colors">
                CAACI
              </button>
              <p className="text-xs text-gray-600 hidden sm:block">{content.footer_tagline}</p>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('services')} className="text-gray-700 hover:text-blue-600 transition-colors text-sm font-medium" data-testid="nav-services">
                {content.nav_services}
              </button>
              <Link to="/ai-operating-system" className="text-gray-700 hover:text-blue-600 transition-colors text-sm font-medium" data-testid="nav-ai-os">
                AI Operating System
              </Link>
              <button onClick={() => scrollToSection('research')} className="text-gray-700 hover:text-blue-600 transition-colors text-sm font-medium" data-testid="nav-research">
                {content.nav_research}
              </button>
              <button onClick={() => scrollToSection('resources')} className="text-gray-700 hover:text-blue-600 transition-colors text-sm font-medium" data-testid="nav-resources">
                {content.nav_resources}
              </button>
              <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-blue-600 transition-colors text-sm font-medium" data-testid="nav-about">
                {content.nav_about}
              </button>
              <Button onClick={() => scrollToSection('contact')} className="bg-black text-white hover:bg-gray-800" data-testid="nav-connect-btn">
                {content.nav_connect}
              </Button>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-md text-gray-700 hover:bg-gray-100"
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 bg-white" data-testid="mobile-menu">
            <div className="px-4 py-3 space-y-3">
              <button onClick={() => scrollToSection('services')} className="block w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-md" data-testid="mobile-nav-services">
                {content.nav_services}
              </button>
              <Link to="/ai-operating-system" className="block w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-md" data-testid="mobile-nav-ai-os">
                AI Operating System
              </Link>
              <button onClick={() => scrollToSection('research')} className="block w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-md" data-testid="mobile-nav-research">
                {content.nav_research}
              </button>
              <button onClick={() => scrollToSection('resources')} className="block w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-md" data-testid="mobile-nav-resources">
                {content.nav_resources}
              </button>
              <button onClick={() => scrollToSection('about')} className="block w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-md" data-testid="mobile-nav-about">
                {content.nav_about}
              </button>
              <Button onClick={() => scrollToSection('contact')} className="w-full bg-black text-white hover:bg-gray-800" data-testid="mobile-nav-connect-btn">
                {content.nav_connect}
              </Button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="hero" className="relative min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 pt-16" data-testid="hero-section">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight" data-testid="hero-heading">
            {content.hero_title}
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto px-4" data-testid="hero-subheading">
            {content.hero_subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button onClick={() => scrollToSection('contact')} className="bg-black text-white hover:bg-gray-800 px-6 py-3 text-base w-full sm:w-auto min-h-[44px]" data-testid="hero-stay-connected-btn">
              {content.hero_cta_primary} <ArrowRight className="ml-2" size={18} />
            </Button>
            <Button onClick={() => scrollToSection('research')} variant="outline" className="px-6 py-3 text-base border-2 w-full sm:w-auto min-h-[44px]" data-testid="hero-explore-research-btn">
              {content.hero_cta_secondary}
            </Button>
          </div>
          <div className="mt-12 sm:mt-16">
            <img 
              src="https://images.unsplash.com/photo-1660165458059-57cfb6cc87e5?w=1200&q=80" 
              alt="AI Neural Network" 
              className="rounded-lg shadow-2xl w-full max-w-4xl mx-auto"
              data-testid="hero-image"
            />
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 bg-gray-50" data-testid="about-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6" data-testid="about-heading">
                {content.about_title}
              </h2>
              <p className="text-base sm:text-lg text-gray-700 mb-4">
                {content.about_description}
              </p>
              <div className="mb-6">
                <h3 className="font-bold text-gray-900 mb-2">{content.about_mission_title}</h3>
                <p className="text-base sm:text-lg text-gray-700">{content.about_mission_text}</p>
              </div>
              <div className="mb-6">
                <h3 className="font-bold text-gray-900 mb-2">{content.about_vision_title}</h3>
                <p className="text-base sm:text-lg text-gray-700">{content.about_vision_text}</p>
              </div>
              <div className="flex items-start space-x-3 text-gray-600">
                <svg className="w-5 h-5 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
                <span className="text-sm sm:text-base">{content.footer_location}, Australia</span>
              </div>
            </div>
            <div>
              <img 
                src="https://images.unsplash.com/photo-1617791160536-598cf32026fb?w=800&q=80" 
                alt="AI Data Visualization" 
                className="rounded-lg shadow-xl w-full"
                data-testid="about-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8" data-testid="services-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4" data-testid="services-heading">
              {content.services_title}
            </h2>
            <p className="text-base sm:text-lg text-gray-600">{content.services_subtitle}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {/* Service Card 1 */}
            <Card className="hover:shadow-xl transition-shadow duration-300" data-testid="service-strategy-card">
              <CardContent className="p-6">
                <img 
                  src="https://images.unsplash.com/photo-1545065695-64b67d551e68?w=600&q=80" 
                  alt="AI Strategy & Consulting" 
                  className="rounded-lg mb-6 w-full h-48 object-cover"
                />
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">{content.service_1_title}</h3>
                <p className="text-gray-600 mb-4 text-sm sm:text-base">
                  {content.service_1_description}
                </p>
                <ul className="space-y-2 text-sm sm:text-base text-gray-700">
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>{content.service_1_bullet_1}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>{content.service_1_bullet_2}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>{content.service_1_bullet_3}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>{content.service_1_bullet_4}</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Service Card 2 */}
            <Card className="hover:shadow-xl transition-shadow duration-300" data-testid="service-integration-card">
              <CardContent className="p-6">
                <img 
                  src="https://images.unsplash.com/photo-1655393001768-d946c97d6fd1?w=600&q=80" 
                  alt="AI Integration & Implementation" 
                  className="rounded-lg mb-6 w-full h-48 object-cover"
                />
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">{content.service_2_title}</h3>
                <p className="text-gray-600 mb-4 text-sm sm:text-base">
                  {content.service_2_description}
                </p>
                <ul className="space-y-2 text-sm sm:text-base text-gray-700">
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>{content.service_2_bullet_1}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>{content.service_2_bullet_2}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>{content.service_2_bullet_3}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>{content.service_2_bullet_4}</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Service Card 3 */}
            <Card className="hover:shadow-xl transition-shadow duration-300" data-testid="service-research-card">
              <CardContent className="p-6">
                <img 
                  src="https://images.unsplash.com/photo-1581090464777-f3220bbe1b8b?w=600&q=80" 
                  alt="Research & Development" 
                  className="rounded-lg mb-6 w-full h-48 object-cover"
                />
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">{content.service_3_title}</h3>
                <p className="text-gray-600 mb-4 text-sm sm:text-base">
                  {content.service_3_description}
                </p>
                <ul className="space-y-2 text-sm sm:text-base text-gray-700">
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>{content.service_3_bullet_1}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>{content.service_3_bullet_2}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>{content.service_3_bullet_3}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>{content.service_3_bullet_4}</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Research & Insights Section */}
      <section id="research" className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 bg-gray-50" data-testid="research-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4" data-testid="research-heading">
              {content.research_title}
            </h2>
            <p className="text-base sm:text-lg text-gray-600">{content.research_subtitle}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {articles.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <p className="text-gray-500 mb-4">No research articles published yet.</p>
                <Link to="/admin">
                  <Button variant="outline">Add Your First Article</Button>
                </Link>
              </div>
            ) : (
              articles.map((article, index) => (
                <Card key={article.id} className="hover:shadow-xl transition-shadow duration-300" data-testid={`research-article-${index + 1}`}>
                  <CardContent className="p-0">
                    {article.imageUrl && (
                      <img 
                        src={article.imageUrl} 
                        alt={article.title} 
                        className="w-full h-48 sm:h-64 object-cover rounded-t-lg"
                      />
                    )}
                    <div className="p-6">
                      <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm text-gray-500 mb-3">
                        <span className={`px-2 py-1 rounded ${getCategoryColor(article.category)}`}>
                          {article.category}
                        </span>
                        <span>{article.readTime} • {article.publishDate}</span>
                      </div>
                      <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3">
                        {article.title}
                      </h3>
                      <p className="text-sm sm:text-base text-gray-600 mb-4">
                        {article.description}
                      </p>
                      {article.articleUrl ? (
                        <a 
                          href={article.articleUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-blue-600 hover:text-blue-700 text-sm font-medium"
                          onClick={() => axios.post(`${API}/articles/${article.id}/view`).catch(() => {})}
                        >
                          Read More <ArrowRight className="ml-1" size={16} />
                        </a>
                      ) : (
                        <Button variant="link" className="p-0 h-auto text-blue-600 hover:text-blue-700">
                          Read More <ArrowRight className="ml-1" size={16} />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          {/* View All Articles Button */}
          {articles.length > 0 && (
            <div className="text-center mt-12">
              <Link to="/archive">
                <Button variant="outline" size="lg" className="min-h-[44px]">
                  View All Research Articles <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Curated Resources Section */}
      <section id="resources" className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8" data-testid="resources-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4" data-testid="resources-heading">
              {content.resources_title}
            </h2>
            <p className="text-base sm:text-lg text-gray-600">{content.resources_subtitle}</p>
          </div>

          <div className="space-y-12">
            {/* Group resources by section */}
            {['Victorian Government AI Resources', 'Australian AI Initiatives', 'Industry Research & Insights'].map(section => {
              const sectionResources = resources.filter(r => r.section === section).slice(0, 6);
              if (sectionResources.length === 0) return null;
              
              return (
                <div key={section}>
                  <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">{section}</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {sectionResources.map(resource => (
                      <a
                        key={resource.id}
                        href={resource.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block p-6 bg-white border border-gray-200 rounded-lg hover:shadow-lg transition-shadow"
                        onClick={() => axios.post(`${API}/resources/${resource.id}/view`).catch(() => {})}
                      >
                        <h4 className="font-bold text-gray-900 mb-2 text-sm sm:text-base">{resource.title}</h4>
                        <p className="text-xs sm:text-sm text-gray-600">{resource.description}</p>
                      </a>
                    ))}
                  </div>
                </div>
              );
            })}

            {resources.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500">No resources available yet. Check back soon!</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-indigo-50" data-testid="contact-section">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4" data-testid="contact-heading">
              {content.contact_title}
            </h2>
            <p className="text-base sm:text-lg text-gray-600">
              {content.contact_subtitle}
            </p>
          </div>

          <Card>
            <CardContent className="p-6 sm:p-8">
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Name *
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleFormChange}
                    placeholder="Your full name"
                    className="w-full min-h-[44px]"
                    data-testid="contact-name-input"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleFormChange}
                    placeholder="your.email@company.com"
                    className="w-full min-h-[44px]"
                    data-testid="contact-email-input"
                  />
                </div>

                <div>
                  <label htmlFor="organization" className="block text-sm font-medium text-gray-700 mb-2">
                    Organization
                  </label>
                  <Input
                    id="organization"
                    name="organization"
                    type="text"
                    value={formData.organization}
                    onChange={handleFormChange}
                    placeholder="Your company or organization"
                    className="w-full min-h-[44px]"
                    data-testid="contact-organization-input"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Message
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    rows={5}
                    value={formData.message}
                    onChange={handleFormChange}
                    placeholder="Tell us about your AI interests or challenges..."
                    className="w-full"
                    data-testid="contact-message-textarea"
                  />
                </div>

                {submitStatus.message && (
                  <div
                    className={`p-4 rounded-lg ${
                      submitStatus.type === 'success'
                        ? 'bg-green-50 text-green-800 border border-green-200'
                        : 'bg-red-50 text-red-800 border border-red-200'
                    }`}
                    data-testid="submit-status-message"
                  >
                    {submitStatus.message}
                  </div>
                )}

                <Button 
                  type="submit" 
                  className="w-full bg-black text-white hover:bg-gray-800 py-6" 
                  data-testid="contact-submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : content.contact_cta} <Mail className="ml-2" size={18} />
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8" data-testid="footer">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12 mb-12">
            {/* Company Info */}
            <div>
              <h3 className="text-xl sm:text-2xl font-bold mb-2">CAACI</h3>
              <p className="text-sm text-gray-400 mb-4">{content.footer_tagline}</p>
              <p className="text-sm text-gray-300">
                {content.footer_description}
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-lg font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <button onClick={() => scrollToSection('services')} className="text-gray-300 hover:text-white transition-colors" data-testid="footer-services-link">
                    {content.nav_services}
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('research')} className="text-gray-300 hover:text-white transition-colors" data-testid="footer-research-link">
                    {content.nav_research}
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('resources')} className="text-gray-300 hover:text-white transition-colors" data-testid="footer-resources-link">
                    {content.nav_resources}
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('about')} className="text-gray-300 hover:text-white transition-colors" data-testid="footer-about-link">
                    {content.nav_about}
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('contact')} className="text-gray-300 hover:text-white transition-colors" data-testid="footer-contact-link">
                    {content.nav_connect}
                  </button>
                </li>
              </ul>
            </div>

            {/* Location */}
            <div>
              <h4 className="text-lg font-bold mb-4">Location</h4>
              <p className="text-sm text-gray-300 mb-2">{content.footer_location}</p>
              <p className="text-sm text-gray-300">{content.footer_region}</p>
            </div>
          </div>

          {/* Bottom Footer */}
          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <p className="text-sm text-gray-400" data-testid="copyright">
                {content.footer_copyright}
              </p>
              <div className="flex items-center gap-4 text-sm text-gray-400">
                <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
                <span>•</span>
                <a href="#" className="hover:text-white transition-colors">Terms of Service</a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Ask CAACI Chat Widget */}
      <AskCAACI />
    </div>
  );
};

// Main App Component with Routing
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/archive" element={<ArchivePage />} />
          <Route path="/ai-operating-system" element={<AiOperatingSystem />} />
          <Route 
            path="/admin" 
            element={
              <ProtectedRoute>
                <AdminPanel />
              </ProtectedRoute>
            } 
          />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;